<template>
  <div class="system-settings">
    <!-- 创建/编辑公告 -->
    <div class="settings-card" v-motion="{ initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.3 } }">
      <h3>{{ editingId ? '编辑公告' : '创建公告' }}</h3>
      <div class="form-group">
        <label>标题</label>
        <input v-model="form.title" placeholder="请输入公告标题" maxlength="200" />
      </div>
      <div class="form-group">
        <label>内容</label>
        <textarea v-model="form.content" ref="contentArea" placeholder="请输入公告内容，支持插入图片" rows="5"></textarea>
        <div class="upload-bar">
          <button class="upload-btn" @click="triggerUpload" :disabled="uploading">{{ uploading ? '上传中...' : '插入图片' }}</button>
          <input type="file" ref="fileInput" accept="image/*" @change="handleFileUpload" style="display:none" />
          <span class="hint">支持 jpg/png/gif/webp，最大10MB</span>
        </div>
        <div class="content-preview" v-if="form.content && hasHtml">
          <label>预览</label>
          <div class="preview-box" v-html="form.content"></div>
        </div>
      </div>
      <div class="form-group">
        <label>启用状态</label>
        <div class="switch-container">
          <label class="switch">
            <input type="checkbox" v-model="form.enabled" />
            <span class="slider"></span>
          </label>
          <span class="switch-label">{{ form.enabled ? '已启用' : '未启用' }}</span>
        </div>
      </div>
      <div class="form-actions">
        <button class="save-btn" @click="saveAnnouncement" :disabled="saving">{{ saving ? '保存中...' : '保存' }}</button>
        <button class="cancel-btn" v-if="editingId" @click="resetForm">取消编辑</button>
      </div>
    </div>

    <!-- 公告列表 -->
    <div class="settings-card" v-motion="{ initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.3, delay: 0.1 } }">
      <h3>公告列表</h3>
      <div v-if="loading" class="loading-text">加载中...</div>
      <div v-else-if="announcements.length === 0" class="empty-text">暂无公告</div>
      <div v-else class="announcement-list">
        <div class="announcement-item" v-for="item in announcements" :key="item.id">
          <div class="item-header">
            <span class="item-title">{{ item.title }}</span>
            <span :class="['status-badge', item.enabled ? 'enabled' : 'disabled']">{{ item.enabled ? '已启用' : '未启用' }}</span>
          </div>
          <div class="item-content" v-html="item.content"></div>
          <div class="item-actions">
            <button class="action-btn edit-btn" @click="editAnnouncement(item)">编辑</button>
            <button class="action-btn toggle-btn" @click="toggleAnnouncement(item)">{{ item.enabled ? '禁用' : '启用' }}</button>
            <button class="action-btn delete-btn" @click="deleteAnnouncement(item)">删除</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch, API_BASE_URL } from '@/config/api'

export default {
  name: 'AnnouncementManagement',
  data() {
    return {
      form: {
        title: '',
        content: '',
        enabled: false
      },
      editingId: null,
      saving: false,
      loading: false,
      uploading: false,
      announcements: []
    }
  },
  mounted() {
    this.loadAnnouncements()
  },
  computed: {
    hasHtml() {
      return /<[a-z][\s\S]*>/i.test(this.form.content)
    }
  },
  methods: {
    triggerUpload() {
      this.$refs.fileInput.click()
    },
    async handleFileUpload(e) {
      const file = e.target.files[0]
      if (!file) return
      this.uploading = true
      try {
        const formData = new FormData()
        formData.append('file', file)
        const token = localStorage.getItem('token')
        const base = API_BASE_URL.replace(/\/$/, '')
        const res = await fetch(base + '/api/admin/upload', {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + token },
          body: formData
        })
        const data = await res.json()
        if (data && data.code === 0 && data.data) {
          const filename = data.data.filename
          const path = data.data.path
          // OSS存储返回完整URL，本地存储用相对路径
          const imgUrl = path && path.startsWith('http') ? path : (base + '/api/files/' + filename)
          const imgTag = '<img src="' + imgUrl + '" />'
          // 在光标处插入，或追加到末尾
          const ta = this.$refs.contentArea
          if (ta && ta.selectionStart !== undefined) {
            const start = ta.selectionStart
            this.form.content = this.form.content.substring(0, start) + '\n' + imgTag + '\n' + this.form.content.substring(start)
          } else {
            this.form.content += '\n' + imgTag
          }
        } else {
          alert(data.msg || '上传失败')
        }
      } catch (err) {
        alert('上传失败')
      }
      this.uploading = false
      e.target.value = ''
    },
    async loadAnnouncements() {
      this.loading = true
      try {
        const res = await apiFetch('/api/announcement/list')
        if (res && res.code === 0) {
          this.announcements = res.data || []
        }
      } catch (e) {
        console.error('加载公告失败', e)
      }
      this.loading = false
    },
    async saveAnnouncement() {
      if (!this.form.title.trim()) {
        alert('请输入公告标题')
        return
      }
      if (!this.form.content.trim()) {
        alert('请输入公告内容')
        return
      }
      this.saving = true
      try {
        const body = {
          title: this.form.title,
          content: this.form.content,
          enabled: this.form.enabled
        }
        if (this.editingId) {
          body.id = this.editingId
        }
        const res = await apiFetch('/api/announcement', {
          method: 'POST',
          body: JSON.stringify(body)
        })
        if (res && res.code === 0) {
          this.resetForm()
          this.loadAnnouncements()
        } else {
          alert(res.msg || '保存失败')
        }
      } catch (e) {
        alert('保存失败')
      }
      this.saving = false
    },
    editAnnouncement(item) {
      this.editingId = item.id
      this.form.title = item.title
      this.form.content = item.content
      this.form.enabled = item.enabled
    },
    async toggleAnnouncement(item) {
      try {
        const res = await apiFetch('/api/announcement/' + item.id + '/toggle', {
          method: 'PUT'
        })
        if (res && res.code === 0) {
          this.loadAnnouncements()
        } else {
          alert(res.msg || '操作失败')
        }
      } catch (e) {
        alert('操作失败')
      }
    },
    async deleteAnnouncement(item) {
      if (!confirm('确定删除该公告吗？')) return
      try {
        const res = await apiFetch('/api/announcement/' + item.id, {
          method: 'DELETE'
        })
        if (res && res.code === 0) {
          if (this.editingId === item.id) {
            this.resetForm()
          }
          this.loadAnnouncements()
        } else {
          alert(res.msg || '删除失败')
        }
      } catch (e) {
        alert('删除失败')
      }
    },
    resetForm() {
      this.editingId = null
      this.form.title = ''
      this.form.content = ''
      this.form.enabled = false
    }
  }
}
</script>

<style scoped>
.system-settings {
  padding: 0;
}

.settings-card {
  background: #fff;
  padding: 24px;
  border: 1px solid #e8e8e8;
  max-width: 500px;
  margin-bottom: 16px;
}

.settings-card h3 {
  margin: 0 0 20px;
  font-size: 15px;
  font-weight: 600;
  color: #111;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  color: #666;
}

.form-group input {
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-group textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
  resize: vertical;
  font-family: inherit;
  line-height: 1.6;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #111;
}

.switch-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #ccc;
  border-radius: 24px;
  transition: 0.2s;
}

.slider:before {
  content: "";
  position: absolute;
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background: #fff;
  border-radius: 50%;
  transition: 0.2s;
}

.switch input:checked + .slider {
  background: #111;
}

.switch input:checked + .slider:before {
  transform: translateX(20px);
}

.switch-label {
  font-size: 13px;
  color: #666;
}

.form-actions {
  display: flex;
  gap: 10px;
}

.save-btn {
  background: #111;
  color: #fff;
  border: none;
  padding: 9px 20px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  font-weight: 500;
}

.save-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cancel-btn {
  background: none;
  color: #666;
  border: 1px solid #ddd;
  padding: 9px 20px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
}

.cancel-btn:hover {
  border-color: #111;
  color: #111;
}

.loading-text,
.empty-text {
  font-size: 13px;
  color: #999;
  text-align: center;
  padding: 20px 0;
}

.announcement-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.announcement-item {
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 14px;
}

.item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.item-title {
  font-size: 14px;
  font-weight: 500;
  color: #111;
}

.status-badge {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
}

.status-badge.enabled {
  background: #e8f5e9;
  color: #2e7d32;
}

.status-badge.disabled {
  background: #f5f5f5;
  color: #999;
}

.item-content {
  font-size: 13px;
  color: #666;
  line-height: 1.5;
  margin-bottom: 10px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 80px;
  overflow: hidden;
}

.item-content img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}

.item-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  font-size: 12px;
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid #ddd;
  background: none;
  color: #666;
  transition: all 0.15s;
}

.action-btn:hover {
  border-color: #111;
  color: #111;
}

.delete-btn:hover {
  border-color: #e53935;
  color: #e53935;
}

.upload-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
}

.upload-btn {
  background: none;
  color: #111;
  border: 1px solid #ddd;
  padding: 5px 14px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}

.upload-btn:hover {
  border-color: #111;
}

.upload-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.content-preview {
  margin-top: 12px;
}

.content-preview label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  color: #999;
}

.preview-box {
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 12px;
  font-size: 14px;
  color: #444;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 200px;
  overflow-y: auto;
}

.preview-box img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 8px 0;
}
</style>
